import React, { useState, useEffect } from 'react';
import api from '../api';
import './KPIs.css';

function KPIs({ mou, canManage, userEmail }) {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const prefix = mou?.prefix;

  const data = mou?.json || mou?.data || {};
  const successMetricsText = data.success_metrics || '';
  let kpis = [];
  if (successMetricsText) {
    kpis = successMetricsText
      .split(/[,;]|\sand\s/i)
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }
  if (kpis.length === 0) {
    kpis = data.kpis || data.key_highlights || [];
  }

  useEffect(() => {
    if (prefix && kpis.length > 0) {
      fetchMetrics();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/mous/${prefix}/kpis`);
      const savedMetrics = res.data || [];
      const initialMetrics = kpis.map((_, i) => {
        const saved = savedMetrics[i] || {};
        return {
          planned: saved.planned || 0,
          inProgress: saved.inProgress || 0,
          completed: saved.completed || 0,
        };
      });
      setMetrics(initialMetrics);
    } catch (err) {
      console.error('Failed to load KPI metrics:', err);
      setError('Failed to load KPI metrics.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    const newMetrics = [...metrics];
    newMetrics[index] = {
      ...newMetrics[index],
      [field]: Math.max(0, parseInt(value) || 0)
    };
    setMetrics(newMetrics);
    setSuccessMsg('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMsg('');
      await api.put(`/api/mous/${prefix}/kpis`, { metrics, userEmail });
      setSuccessMsg('Metrics saved successfully!');
    } catch (err) {
      console.error('Failed to save KPI metrics:', err);
      const serverMsg = err.response?.data?.detail || err.response?.data?.error || '';
      setError(`Failed to save KPI metrics. ${serverMsg || 'Check permissions.'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kpis-container">
      <div className="kpis-header">
        <h3>📊 Key Performance Indicators</h3>
        {canManage && (
          <p className="kpis-subtitle">Manually update progress for each KPI and click Save.</p>
        )}
      </div>

      {kpis.length === 0 ? (
        <div className="no-kpis">
          📈 No KPIs or success metrics defined in this MoU yet.
          {canManage && <p>Success metrics can be added by editing the MoU JSON file.</p>}
        </div>
      ) : loading ? (
        <div className="loading-spinner">Loading metrics...</div>
      ) : (
        <>
          <div className="kpis-list">
            {kpis.map((kpi, index) => (
              <div key={index} className="kpi-card">
                <div className="kpi-number">{index + 1}</div>
                <div className="kpi-content">
                  <p className="kpi-text">{kpi}</p>

                  <div className="kpi-tracking">
                    <div className="kpi-metric-inputs">
                      <div className="metric-input-group">
                        <label>Planned:</label>
                        <input
                          type="number"
                          min="0"
                          value={metrics[index]?.planned || 0}
                          onChange={(e) => handleChange(index, 'planned', e.target.value)}
                          className="metric-input"
                          disabled={!canManage}
                        />
                      </div>
                      <div className="metric-input-group">
                        <label>In Progress:</label>
                        <input
                          type="number"
                          min="0"
                          value={metrics[index]?.inProgress || 0}
                          onChange={(e) => handleChange(index, 'inProgress', e.target.value)}
                          className="metric-input"
                          disabled={!canManage}
                        />
                      </div>
                      <div className="metric-input-group">
                        <label>Completed:</label>
                        <input
                          type="number"
                          min="0"
                          value={metrics[index]?.completed || 0}
                          onChange={(e) => handleChange(index, 'completed', e.target.value)}
                          className="metric-input"
                          disabled={!canManage}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {canManage && (
            <div className="kpis-footer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
              {error && <div className="error-message">{error}</div>}
              {successMsg && <div className="success-message" style={{ color: 'green', fontWeight: 'bold' }}>{successMsg}</div>}
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                {saving ? 'Saving...' : 'Save Metrics'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default KPIs;
